import { getPlatform } from "../platform/platformRef";
import type { AccessSession } from "../api/projectAccess";
import { clearOnSignOut } from "../utils/signOutCleanup";
/** A server-issued, process-local lease. Nothing restored from disk is authority.
 * A restart, identity/placement change, clock rollback or expired lease requires
 * online admission. Policy version is independent of PP-827 route generation.
 */
export interface ResourceAccess extends Partial<AccessSession> {
    project: string;
    organization_id?: string;
    member_id?: string;
    policy_version: number;
    active: boolean;
    enforced: boolean;
    role: "admin" | "internal" | "restricted";
    job_role_id?: string | null;
    effective_actions?: string[];
    can_manage_legacy?: boolean;
    offline_lease_seconds: number;
    online_lease_seconds?: number;
}
export interface AccessTicket {
    scope: string | null;
    epoch: number;
}
type Lease = ResourceAccess & {
    wall: number;
    monotonic: number;
    duration: number;
    admitted: boolean;
};
let scope: string | null = null;
let lease: Lease | null = null;
let epoch = 0;
// Request fencing also advances when a timed lease expires. Cached UI state is
// retired only after authority changes; expiry temporarily hides it for renewal.
let authorityEpoch = 0;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());
// Native revocation is ordered and acknowledged before a replacement admission.
// Retain failures for the admission owner to retry; never silently mount against
// a native host whose old authority could still be live.
let nativeInvalidation: Promise<void> = Promise.resolve();
const invalidateNative = () => {
    let invalidate: (() => Promise<void>) | undefined;
    try {
        invalidate = getPlatform().auth?.invalidateProjectAccess;
    } catch {
        // No installed native host yet: it still starts without admission.
        return;
    }
    if (!invalidate) return;
    nativeInvalidation = nativeInvalidation.catch(() => undefined).then(() => invalidate!());
    // The owner below observes rejection. Attach a handler immediately so an
    // invalidation from a response interceptor cannot be an unhandled rejection.
    void nativeInvalidation.catch(() => undefined);
};

export async function synchronizeNativeResourceAccess(): Promise<void> {
    let pending = nativeInvalidation;
    try {
        await pending;
    } catch {
        // One retry per admission attempt. Continued failure stays fail-closed
        // and is recoverable through the normal Retry / reconnect loop.
        invalidateNative();
        pending = nativeInvalidation;
        await pending;
    }
    while (pending !== nativeInvalidation) {
        pending = nativeInvalidation;
        await pending;
    }
}

export const subscribeResourceAccess = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};
export const resourceAccessEpoch = () => epoch;
export const resourceAuthorityEpoch = () => authorityEpoch;
export const resourceAccessState = () => lease;
/** The committed reader of this document: tenant, API base and member, as the tenant query
 *  client keys its cache (`tenantCacheScope`). Null until a page is admitted with a member. */
export const resourceAccessScope = () => scope;
export const captureAccessTicket = (): AccessTicket => ({ scope, epoch });
export const accessTicketIsCurrent = (ticket: AccessTicket) =>
    ticket.scope === scope && ticket.epoch === epoch;

export function setResourceAccessScope(next: string | null): void {
    if (next === scope) return;
    // Native hosts admit their own identity before a renderer mounts. The
    // renderer's first association is not a revocation of that verified lease
    // (nor permission to reuse it for another native identity).
    const initialAssociation = scope === null && lease === null && next !== null;
    scope = next;
    lease = null;
    epoch += 1;
    authorityEpoch += 1;
    if (!initialAssociation) invalidateNative();
    notify();
}

function authority(value: ResourceAccess): string {
    return JSON.stringify({
        project: value.project,
        organization: value.organization_id,
        member: value.member_id,
        principal: value.principal_id,
        company: value.directory_company_id,
        version: value.policy_version,
        active: value.active,
        enforced: value.enforced,
        protectedDownloads: value.protected_downloads,
        enforcer: value.required_enforcer_version,
        client: value.required_client_version,
        role: value.role,
        template: value.job_role_id,
        actions: value.effective_actions?.slice().sort(),
        tools: Object.entries(value.effective_tool_permissions ?? {})
            .sort()
            .map(([tool, actions]) => [tool, actions.slice().sort()]),
        manage: value.can_manage,
        legacy: value.can_manage_legacy,
        capabilities: Object.entries(value.capabilities ?? {}).sort(),
    });
}

/** `receivedAt` (ms, this clock) is when the answer arrived, for one read later than that
 *  (company-session's copy of `/me`): the lease counts from its arrival, never from now. */
export function acceptResourceAccess(
    value: ResourceAccess,
    ticket: AccessTicket,
    receivedAt?: number,
): void {
    if (!accessTicketIsCurrent(ticket))
        throw new Error("Project access changed during this request");
    if (
        !Number.isSafeInteger(value.policy_version) ||
        value.policy_version < 0 ||
        typeof value.project !== "string" ||
        !value.project ||
        typeof value.active !== "boolean" ||
        typeof value.enforced !== "boolean" ||
        !["admin", "internal", "restricted"].includes(value.role) ||
        !Number.isFinite(value.offline_lease_seconds) ||
        (value.online_lease_seconds !== undefined &&
            (!Number.isFinite(value.online_lease_seconds) || value.online_lease_seconds <= 0))
    )
        throw new Error("Invalid project access response");
    if (lease && (lease.project !== value.project || value.policy_version < lease.policy_version)) {
        throw new Error("A stale project access response was discarded");
    }
    const changed = lease !== null && authority(lease) !== authority(value);
    admissionUnavailable = null;
    const now = Date.now();
    const age = receivedAt === undefined ? 0 : Math.max(0, now - receivedAt);
    lease = {
        ...value,
        admitted: true,
        duration:
            Math.max(0, Math.min(MAX_OFFLINE_LEASE_SECONDS, value.offline_lease_seconds)) * 1000,
        wall: now - age,
        monotonic: performance.now() - age,
    };
    if (changed) {
        epoch += 1;
        authorityEpoch += 1;
        invalidateNative();
    }
    notify();
}

export function observeAccessVersion(project: string, version: number, ticket: AccessTicket): void {
    if (!accessTicketIsCurrent(ticket))
        throw new Error("Project access changed during this request");
    if (!lease) return;
    if (lease.project !== project || version < lease.policy_version)
        throw new Error("A stale project response was discarded");
    if (version > lease.policy_version) {
        // Hide cached content until /me provides the new active role and lease.
        lease = { ...lease, policy_version: version, admitted: false, duration: 0 };
        epoch += 1;
        authorityEpoch += 1;
        invalidateNative();
        notify();
    }
}

/** Offline use follows the server's offline lease for every role; protected
 * projects cache and work offline as unprotected ones do (D8, 2026-09-28). */
export function offlineAccessAllowed(): boolean {
    if (!lease?.active || !lease.admitted || lease.duration <= 0) return false;
    const elapsed = leaseElapsed(lease);
    return elapsed !== null && elapsed < lease.duration;
}

/** The ceiling on the server's offline window: 7 days, which the server sends
 * for every project and role (D8). */
export const MAX_OFFLINE_LEASE_SECONDS = 604_800;
/** A wall clock this far behind the monotonic one has been set back. */
const CLOCK_ROLLBACK_TOLERANCE_MS = 60_000;

/** Time since admission, or null when the wall clock was rolled back. Sleep
 * pauses `performance.now()` on WebKit and a forward wall jump is
 * indistinguishable from time passing, so the larger reading counts: both only
 * shorten the window, never extend it. */
function leaseElapsed(value: Lease): number | null {
    const wall = Date.now() - value.wall;
    const monotonic = performance.now() - value.monotonic;
    if (!Number.isFinite(wall) || !Number.isFinite(monotonic) || monotonic < 0) return null;
    if (wall + CLOCK_ROLLBACK_TOLERANCE_MS < monotonic) return null;
    return Math.max(wall, monotonic);
}

/** How the last admission failed. A transport failure (no response) stands in
 * for being offline for as long as it lasts. A server fault (5xx or 429 with no
 * access decision) says the service is unwell, not that access ended, so it
 * stands in only for `SERVER_FAULT_GRACE_MS` from its first occurrence; the
 * same bound as iOS (`NativeResourceAccess.serverFaultGrace`). */
let admissionUnavailable: { transport: boolean; since: number } | null = null;
export const SERVER_FAULT_GRACE_MS = 3_600_000;

/** Record how the last admission failed: `true` or "transport" for no response
 * (captive portal, VPN down, outage), "fault" for a 5xx/429 with no access
 * decision, `false` to clear. It selects the budget only; renewal keeps trying.
 * Any success or definitive answer clears it. A repeated fault keeps its first
 * timestamp so a sustained one cannot hold the long budget past the grace. */
export function setAdmissionUnavailable(value: boolean | "transport" | "fault"): void {
    if (value === false) {
        admissionUnavailable = null;
        return;
    }
    admissionUnavailable = {
        transport: value !== "fault",
        since: admissionUnavailable?.since ?? Date.now(),
    };
}

function serverUnreachable(): boolean {
    if (!admissionUnavailable) return false;
    if (admissionUnavailable.transport) return true;
    const age = Date.now() - admissionUnavailable.since;
    return age >= 0 && age < SERVER_FAULT_GRACE_MS;
}

/** Whether the online budget applies: the device reports a network and the
 * last admission did not fail for want of a server. */
const serverReachable = () =>
    (typeof navigator === "undefined" || navigator.onLine) && !serverUnreachable();

/** The renewal owner checks the lease this often; see `accessRenewalDue`. */
export const ACCESS_RENEWAL_TICK_MS = 30_000;
/** An unprotected project's online lease, held for two 5-minute renewals. */
const UNPROTECTED_ONLINE_LEASE_MS = 600_000;

/** A protected or enforced project keeps the server's short online lease (at
 * most 60 s), renewed every 30 s. An unprotected project has no restricted
 * content to hide: its lease only bounds how long an idle page takes to notice
 * activation, and after activation every response carries the new policy
 * version, which suspends the lease at once (`observeAccessVersion`). */
function onlineLeaseMs(value: Lease): number {
    if (!value.enforced && !value.protected_downloads) return UNPROTECTED_ONLINE_LEASE_MS;
    return Math.max(1, Math.min(60, value.online_lease_seconds ?? 60)) * 1000;
}

/** Whether the periodic tick should ask the server again: once the lease would
 * pass half its online duration before the next tick. That is every tick (30 s)
 * for a protected or enforced project and every 5 minutes otherwise. Focus and
 * reconnect renew regardless; a missing, denied or suspended lease always renews. */
export function accessRenewalDue(): boolean {
    if (!lease?.active || !lease.admitted) return true;
    const age = Math.max(Date.now() - lease.wall, performance.now() - lease.monotonic);
    return age + ACCESS_RENEWAL_TICK_MS > onlineLeaseMs(lease) / 2;
}

/** Mounted online content has a short lease too. Time alone must hide it,
 * even if no query happens to run and no server rejection has arrived. */
export function accessLeaseExpired(): boolean {
    if (!lease?.active) return false;
    if (!lease.admitted) return true;
    const elapsed = leaseElapsed(lease);
    const duration = serverReachable() ? onlineLeaseMs(lease) : lease.duration;
    return elapsed === null || elapsed >= duration;
}

/** A clock/lease expiry proves only that revalidation is required. Fence old
 * responses and native reads, while retaining the authority fingerprint for a
 * same-authority renewal. Definitive denial still retires all cached content.
 */
export function suspendResourceAccess(): void {
    if (!lease?.active || !lease.admitted) return;
    lease = { ...lease, admitted: false };
    epoch += 1;
    invalidateNative();
    notify();
}

/** Online expiry suspends a lease until the server answers again; it does not
 * retire the offline grant behind it. Once the server is out of reach (the
 * device is offline, or admission got no response or a server fault within its
 * grace), a suspended lease is admitted again for what is left of its original
 * offline window: the admission time never moves, so the horizon stays where
 * the server put it. A policy change zeroes the window and a denial retires the
 * lease, so neither can resume. Returns whether the lease is admitted. */
export function resumeOfflineResourceAccess(): boolean {
    if (!lease?.active) return false;
    if (lease.admitted) return true;
    if (lease.duration <= 0 || serverReachable()) return false;
    const elapsed = leaseElapsed(lease);
    if (elapsed === null || elapsed >= lease.duration) return false;
    lease = { ...lease, admitted: true };
    notify();
    return true;
}

/** Definitive admission rejection fences in-flight data even without headers. */
export function rejectResourceAccess(): void {
    admissionUnavailable = null;
    if (!lease?.active) return;
    lease = { ...lease, active: false, admitted: false, duration: 0 };
    epoch += 1;
    authorityEpoch += 1;
    invalidateNative();
    notify();
}

export function invalidateResourceAccess(): void {
    lease = null;
    admissionUnavailable = null;
    epoch += 1;
    authorityEpoch += 1;
    invalidateNative();
    notify();
}

// Sign-out drops the lease and every in-flight admission (registered here: logout cannot import this module).
clearOnSignOut(invalidateResourceAccess);

/** Whether the admitted project enforces access rules (enforced activation). */
export const hasProtectedProject = () => lease?.enforced === true;

/** Stable 64-bit digest (two FNV-style lanes) for durable cache namespaces. */
function digest(value: string): string {
    let a = 0x811c9dc5;
    let b = 0x9e3779b9;
    for (let index = 0; index < value.length; index++) {
        const code = value.charCodeAt(index);
        a = Math.imul(a ^ code, 0x01000193);
        b = Math.imul(b ^ code, 0x5bd1e995);
        b ^= b >>> 15;
    }
    return (a >>> 0).toString(16).padStart(8, "0") + (b >>> 0).toString(16).padStart(8, "0");
}

/** A protected project's durable caches (the persisted query snapshot, the
 * native replay store) are namespaced by the admitted authority: member,
 * principal, role, policy version, actions and tools. Anything written under
 * one authority is never read under another, so a role downgrade, a revoked
 * grant or any policy change cannot revive data from before it (D8). Stable
 * across reloads, unlike the process-local epochs. Null on an unprotected
 * project (its caches keep main's keys) or without a lease. */
export function protectedCacheAuthority(): string | null {
    return lease?.enforced === true ? digest(authority(lease)) : null;
}
